import React from "react";
import useModules from "../hooks/useModules";
import { Building2 } from "lucide-react";

/**
 * Iter60.p13 — Module context chip.
 *
 * When rendered inside a module workspace (`/m/{code}/*`) this replaces
 * the "Module" dropdown filter. The dropdown was letting scoped users
 * see "All modules" / other modules as an option — visually confusing
 * and functionally leaky. Callers should conditionally render this
 * chip when `scopedId` is truthy, and fall back to the FilterSelect
 * dropdown elsewhere (admin cross-module views, `/admin/*`, etc.).
 *
 * Usage:
 *   {scopedId
 *      ? <ModuleContextChip moduleId={scopedId} testid="staff-module-chip" />
 *      : <FilterSelect label="Module" ... />
 *   }
 */
export default function ModuleContextChip({ moduleId, testid, size = "md" }) {
    const { byId } = useModules();
    const mod = byId?.[moduleId];
    if (!mod) return null;

    const isSmall = size === "sm";
    const pad = isSmall ? "px-2.5 py-1" : "px-3 py-1.5";
    const font = isSmall ? "text-[11px]" : "text-xs";
    const iconSize = isSmall ? "h-3 w-3" : "h-3.5 w-3.5";
    const gap = isSmall ? "gap-1.5" : "gap-2";

    return (
        <div className="flex flex-col" data-testid={testid || "module-context-chip"}>
            {!isSmall && (
                <label className="asl-label text-[11px]" data-testid={`${testid || "module-context-chip"}-label`}>
                    Module
                </label>
            )}
            <div
                className={`inline-flex items-center ${gap} rounded-md border ${pad} ${font} font-medium asl-navy-text bg-white`}
                style={{
                    borderColor: "var(--asl-navy-200, #c7cddf)",
                    background: "linear-gradient(180deg, #f9fafc 0%, #eef1f7 100%)",
                    minWidth: 140,
                }}
                title={`Scoped to ${mod.name} — cannot switch modules from this page.`}
                data-testid={`${testid || "module-context-chip"}-value`}
                aria-label={`Module context: ${mod.name}`}
            >
                <Building2 className={iconSize} aria-hidden="true" />
                <span className="truncate">{mod.name}</span>
                <span
                    className="ml-1 px-1.5 py-0.5 rounded text-[9px] tracking-wider uppercase"
                    style={{
                        background: "var(--asl-navy-50, #eef1f7)",
                        color: "var(--asl-navy-700, #1B2A56)",
                        border: "1px solid var(--asl-navy-100, #dde2ee)",
                    }}
                    data-testid={`${testid || "module-context-chip"}-code`}
                >
                    {mod.code}
                </span>
            </div>
        </div>
    );
}

/**
 * Iter60.p13 — Larger heading-style module context indicator.
 * Used at the top of pages that need extra prominence (Staff).
 */
export function ModuleContextHeader({ moduleId, subtitle, testid }) {
    const { byId } = useModules();
    const mod = byId?.[moduleId];
    if (!mod) return null;
    return (
        <div
            className="flex items-center gap-3 px-4 py-2.5 rounded-md border"
            style={{
                background: "linear-gradient(180deg, rgba(27,42,86,0.03) 0%, rgba(27,42,86,0.08) 100%)",
                borderColor: "var(--asl-navy-100, #dde2ee)",
            }}
            data-testid={testid || "module-context-header"}
        >
            <div
                className="h-9 w-9 rounded-md flex items-center justify-center asl-crimson-bg text-white"
                aria-hidden="true"
            >
                <Building2 className="h-4 w-4" />
            </div>
            <div className="leading-tight">
                <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]" data-testid={`${testid || "module-context-header"}-eyebrow`}>
                    Module Workspace
                </div>
                <div className="text-sm font-semibold asl-navy-text flex items-center gap-2" data-testid={`${testid || "module-context-header"}-name`}>
                    {mod.name}
                    <span
                        className="px-1.5 py-0.5 rounded text-[10px] tracking-wider uppercase font-semibold"
                        style={{
                            background: "var(--asl-navy-50, #eef1f7)",
                            color: "var(--asl-navy-700, #1B2A56)",
                            border: "1px solid var(--asl-navy-100, #dde2ee)",
                        }}
                    >
                        {mod.code}
                    </span>
                </div>
                {subtitle && (
                    <div className="text-[11px] text-[color:var(--asl-muted)] mt-0.5" data-testid={`${testid || "module-context-header"}-subtitle`}>
                        {subtitle}
                    </div>
                )}
            </div>
        </div>
    );
}
